import React, { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../redux";
import { dismissTestNotification } from "../redux/reducers/testNotificationsSlice";
import { View, Card, Text, Spinner } from "./base";
import { Icon } from "./base/Icon";
import { Agent } from "../redux/reducers/agentSlice";

interface OrganizationTestNotificationsProps {
  agentIds: string[];
  agentEntities: Record<string, Agent>;
  onTestSelect: (agentId: string, testName: string) => void;
}

export const OrganizationTestNotifications: React.FC<OrganizationTestNotificationsProps> = ({ agentIds, agentEntities, onTestSelect }) => {
  const dispatch = useAppDispatch();
  const testNotifications = useAppSelector((state) => (state.testNotifications as any).notifications);
  const isLoading = useAppSelector((state) => (state.testNotifications as any).isLoading);

  // TODO: Uncomment this when we have a better way to fetch notifications
  // useEffect(() => {
  //   const fetchAllNotifications = async () => {
  //     // Use Promise.all to fetch all notifications in parallel
  //     await Promise.all(agentIds.map((agentId) => dispatch(fetchTestNotifications(agentId))));
  //   };

  //   if (agentIds.length > 0) {
  //     fetchAllNotifications();
  //   }
  // }, [agentIds.join(","), dispatch]); // Use join to create a stable dependency

  const handleNotificationClick = async (agentId: string, testName: string) => {
    try {
      await dispatch(dismissTestNotification({ agentId, testName })).unwrap();

      // Navigate to the test
      onTestSelect(agentId, testName);
    } catch (error) {
      console.error("Failed to dismiss notification:", error);
    }
  };

  // Memoize the notifications collection to prevent unnecessary re-renders
  const allNotifications = useMemo(() => {
    const notifications: Array<{ agentId: string; testName: string; agentName: string }> = [];

    agentIds.forEach((agentId) => {
      const agentNotifications = testNotifications[agentId] || [];
      const agent = agentEntities[agentId];
      const agentName = agent?.name || `Agent ${agentId}`;

      agentNotifications.forEach((testName: string) => {
        notifications.push({ agentId, testName, agentName });
      });
    });

    return notifications;
  }, [agentIds, testNotifications, agentEntities]);

  const isAnyLoading = useMemo(() => {
    return Object.values(isLoading).some(Boolean);
  }, [isLoading]);

  if (isAnyLoading) {
    return (
      <View className="flex justify-center py-4">
        <Spinner
          size={24}
          variant="brand"
        />
      </View>
    );
  }

  if (allNotifications.length === 0) {
    return null; // Don't show anything if there are no notifications
  }

  return (
    <View>
      <View className="mb-2 flex items-center justify-between">
        <Text className="text-lg font-semibold">Test Notifications</Text>
        <Text className="text-sm text-gray-500 dark:text-gray-400">
          {allNotifications.length} test{allNotifications.length !== 1 ? "s" : ""} completed
        </Text>
      </View>
      <View className="space-y-3">
        {allNotifications.map(({ agentId, testName, agentName }, index) => (
          <View
            key={`${agentId}-${testName}-${index}`}
            className="cursor-pointer"
            onClick={() => handleNotificationClick(agentId, testName)}
          >
            <Card className="border-2 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors">
              <View className="p-2">
                <View className="flex items-center gap-2">
                  <Icon
                    name="notification"
                    className="w-4 h-4 text-yellow-800 dark:text-yellow-200"
                  />
                  <Text className="text-yellow-800 dark:text-yellow-200 font-medium">
                    <span className="font-semibold">Test Complete:</span> Test <span className="font-bold">{testName}</span> for agent{" "}
                    <span className="font-bold">{agentName}</span> has finished
                  </Text>
                </View>
              </View>
            </Card>
          </View>
        ))}
      </View>
    </View>
  );
};
